import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';
import { db } from '../../firebase';
import {
    arrayUnion,
    doc,
    getDoc,
    serverTimestamp,
    setDoc,
    updateDoc
} from 'firebase/firestore';
import './VideoSuccessPage.css';

const MAX_AUTH_ATTEMPTS = 8;

const VideoSuccessPage = () => {
    const { user } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [videoId, setVideoId] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const [videoDetails, setVideoDetails] = useState(null);
    const [alreadyRecorded, setAlreadyRecorded] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sid = params.get('session_id') || sessionStorage.getItem('videoSessionId');
        const uid = params.get('user_id') || sessionStorage.getItem('videoUserId');
        const vid = params.get('video_id') || sessionStorage.getItem('videoId');

        if (sid) {
            setSessionId(sid);
            sessionStorage.setItem('videoSessionId', sid);
        }
        if (uid) {
            setUserId(uid);
            sessionStorage.setItem('videoUserId', uid);
        }
        if (vid) {
            setVideoId(vid);
            sessionStorage.setItem('videoId', vid);
        }

        if (user && sid && vid) {
            recordPurchase(user.uid, vid, sid);
        } else if (!user && sid && attempts < MAX_AUTH_ATTEMPTS) {
            const timer = setTimeout(() => {
                setAttempts(prev => prev + 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [location, user, attempts]);

    const recordPurchase = async (uid, vid, sid) => {
        try {
            setLoading(true);
            setError(null);

            if (!uid || !vid || !sid) {
                throw new Error('Missing purchase details.');
            }

            const userRef = doc(db, 'users', uid);
            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) {
                throw new Error('User account not found.');
            }

            const videoRef = doc(db, 'video_feed', vid);
            const videoSnap = await getDoc(videoRef);
            if (!videoSnap.exists()) {
                throw new Error('Video not found.');
            }

            const videoData = videoSnap.data();

            const purchaseRef = doc(db, 'users', uid, 'purchased_video_links', vid);
            const purchaseSnap = await getDoc(purchaseRef);
            if (purchaseSnap.exists()) {
                setAlreadyRecorded(true);
            }

            await setDoc(purchaseRef, {
                videoId: vid,
                title: videoData.title || 'Video',
                description: videoData.description || '',
                vimeoId: videoData.vimeoId || null,
                vimeoUrl: videoData.vimeoUrl || null,
                price: typeof videoData.price === 'number' ? videoData.price : Number(videoData.price) || null,
                thumbnail: videoData.thumbnail || null,
                purchasedAt: serverTimestamp(),
                checkoutSessionId: sid
            }, { merge: true });

            await updateDoc(userRef, {
                purchasedVideoIds: arrayUnion(vid)
            });

            // Add purchaser email to video document
            const userEmail = user?.email;
            if (userEmail) {
                const currentPurchaserEmails = videoData.purchaserEmails || [];
                if (!currentPurchaserEmails.includes(userEmail)) {
                    await updateDoc(videoRef, {
                        purchaserEmails: arrayUnion(userEmail)
                    });
                }
            }

            setVideoDetails({ id: vid, ...videoData });
            setLoading(false);
        } catch (err) {
            console.error('Error recording video purchase:', err);
            setError(err.message || 'Unable to record video purchase.');
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!loading && !error) {
            const container = document.querySelector('.video-success-container');
            if (container) {
                setTimeout(() => container.classList.add('show'), 100);
            }
        }
    }, [loading, error]);

    const handleViewFeed = () => {
        navigate('/auth-home', {
            state: { targetComponent: 'videos' },
            replace: true
        });
    };

    if (loading) {
        return (
            <div className="video-success-page">
                <div className="video-success-container loading-container show">
                    <h2>Finalizing your purchase...</h2>
                    <div className="loading-spinner"></div>
                    <p>Please wait while we prepare your video access.</p>
                    {attempts >= 3 && (
                        <button onClick={handleViewFeed} className="video-return-button">
                            Return to Dashboard
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="video-success-page">
                <div className="video-success-container error">
                    <h2>Something went wrong</h2>
                    <p>{error}</p>
                    <div className="error-details">
                        <p>Session ID: {sessionId || 'Unavailable'}</p>
                        <p>User ID: {userId || user?.uid || 'Unavailable'}</p>
                        <p>Video ID: {videoId || 'Unavailable'}</p>
                    </div>
                    <Link to="/videos" className="video-return-button">
                        Back to Videos
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="video-success-page">
            <div className="video-success-container">
                <div className="video-success-icon">
                    <svg viewBox="0 0 24 24" width="100" height="100">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                </div>
                <h2>Video Purchase Confirmed!</h2>
                <div className="video-success-message">
                    <p>Thank you for your purchase.</p>
                    <p>Your video access is now linked to your account.</p>
                </div>

                {alreadyRecorded && (
                    <p className="video-success-note">Access to this video should be granted, if any issues arise, please email dannytopia.llc@gmail.com with your issue.</p>
                )}

                {videoDetails && (
                    <div className="video-order-details">
                        <h3>Video Details</h3>
                        <div className="detail-item">
                            <span className="detail-label">Title:</span>
                            <span className="detail-value">{videoDetails.title || 'Video'}</span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Video ID:</span>
                            <span className="detail-value">{videoDetails.id}</span>
                        </div>
                        {videoDetails.description && (
                            <div className="detail-item">
                                <span className="detail-label">Description:</span>
                                <span className="detail-value">{videoDetails.description}</span>
                            </div>
                        )}
                        {typeof videoDetails.price !== 'undefined' && videoDetails.price !== null && (
                            <div className="detail-item">
                                <span className="detail-label">Price:</span>
                                <span className="detail-value">${Number(videoDetails.price).toFixed(2)}</span>
                            </div>
                        )}
                        <div className="detail-item">
                            <span className="detail-label">Order ID:</span>
                            <span className="detail-value">{sessionId?.substring(0, 10)}...</span>
                        </div>
                    </div>
                )}

                <div className="video-next-steps">
                    <h3>Next Steps</h3>
                    <ul>
                        <li>Your purchased video now appears as owned in your feed.</li>
                        <li>Playback features are coming soon. Stay tuned!</li>
                    </ul>
                </div>

                <button onClick={handleViewFeed} className="video-return-button">
                    Go to My Video Feed
                </button>
            </div>
        </div>
    );
};

export default VideoSuccessPage;
